import React, { useCallback, useState } from "react";
import api from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import RefreshControl from "@/components/RefreshControl";
import useAutoRefresh from "@/lib/useAutoRefresh";
import { Package, ListChecks, Barcode, User } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <Card className="p-6 border-zinc-200" data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">{label}</div>
          <div className="mt-2 text-4xl font-heading font-black tabular-nums">{value}</div>
        </div>
        <div className="w-12 h-12 rounded-md flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
          <Icon size={24} weight="bold" />
        </div>
      </div>
    </Card>
  );
}

export default function Reports() {
  const [stats, setStats] = useState(null);
  const [mode, setMode] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([
        api.get("/stats"),
        api.get("/system/mode"),
      ]);
      setStats(s.data);
      setMode(m.data);
    } finally { setLoading(false); }
  }, []);

  const { refresh, refreshing, lastUpdatedAt } = useAutoRefresh(fetchAll);

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">From backend stats</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl mt-1">Reports</h1>
          <p className="text-sm text-zinc-600 mt-1">
            Live counts of orders, items, and scans in the current{" "}
            <span className="font-semibold uppercase tracking-widest text-xs">{mode?.mode || "—"}</span> mode.
          </p>
        </div>
        <RefreshControl
          onRefresh={refresh}
          refreshing={refreshing}
          lastUpdatedAt={lastUpdatedAt}
          testId="reports-refresh"
        />
      </div>

      {loading ? (
        <Card className="p-12 text-center text-zinc-500">Loading…</Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Orders" value={stats?.total_orders ?? 0} icon={Package} accent="#C8102E" />
            <StatCard label="Total Items" value={stats?.total_items ?? 0} icon={ListChecks} accent="#78350F" />
            <StatCard label="Total Scans" value={stats?.total_scans ?? 0} icon={Barcode} accent="#3B82F6" />
            <StatCard label="My Scans" value={stats?.my_scans ?? 0} icon={User} accent="#10B981" />
          </div>

          {mode && (
            <Card className="p-6 border-zinc-200" data-testid="base44-info-card">
              <h2 className="font-heading font-bold text-xl mb-4">Base44 sync · {mode.mode}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-1">Live environment</div>
                  <div className="font-mono text-xs truncate">{mode.base44_live?.url || "—"}</div>
                  <div className="text-xs text-zinc-500">
                    API key: {mode.base44_live?.has_api_key ? "✓ configured" : "✗ missing"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-1">Test environment</div>
                  <div className="font-mono text-xs truncate">{mode.base44_test?.url || "—"}</div>
                  <div className="text-xs text-zinc-500">
                    API key: {mode.base44_test?.has_api_key ? "✓ configured" : "✗ missing"}
                  </div>
                </div>
              </div>
              {mode.last_changed_at && (
                <div className="mt-4 pt-3 border-t border-zinc-200 text-xs text-zinc-500">
                  Mode last changed {new Date(mode.last_changed_at).toLocaleString()}
                  {mode.last_changed_by_email && ` by ${mode.last_changed_by_email}`}
                </div>
              )}
            </Card>
          )}

          <Card className="p-6 border-zinc-200">
            <h2 className="font-heading font-bold text-lg mb-2">Detailed analytics</h2>
            <p className="text-sm text-zinc-600 mb-4">
              The mobile app's backend tracks every scan event. For deeper analytics, the desktop
              app uses the same data — drill down by jumping to{" "}
              <Link to="/orders" className="text-[#C8102E] hover:underline font-semibold">Orders</Link>{" "}
              and filtering by status.
            </p>
          </Card>
        </>
      )}
    </div>
  );
}
